import React, { ReactNode, RefObject, useEffect, useLayoutEffect, useState } from "react";
import { VideoDockToggle, type VideoDockView } from "./VideoDockToggle";

type RightPanelHostProps = {
  rightPanelMode: "video" | "social_posts" | "wordpress_blog" | null;
  /** Increments each time the user explicitly requests the canvas view (e.g. "Open Canvas" button). */
  canvasRequestSignal?: number;
  splitContainerRef: RefObject<HTMLDivElement>;
  chatWidthPercent: number;
  setIsResizing: (value: boolean) => void;
  splitResizeClass: string;
  surfaceBg: string;
  chatContent: ReactNode;
  videoPanel: ReactNode;
  socialPostsPanel: ReactNode;
  /** Rendered when rightPanelMode === "wordpress_blog". May be null until a draft exists. */
  wordpressBlogPanel?: ReactNode;
  /** Video Creator panel: which layer is visible (controlled by parent for header toggle). */
  videoDockView?: VideoDockView;
  onVideoDockViewChange?: (view: VideoDockView) => void;
};

/* ────────────────────────────────────────────────────────────
   Segmented Control — pill toggle: Chat | Canvas / Video / Blog
   ──────────────────────────────────────────────────────────── */
const SegmentedControl: React.FC<{
  active: "chat" | "side";
  onChange: (v: "chat" | "side") => void;
  /** Right tab label */
  sideLabel: "Video" | "Canvas" | "Blog";
  sideMaterialIcon: "movie" | "dashboard" | "article";
}> = ({ active, onChange, sideLabel, sideMaterialIcon }) => (
  <div className="flex shrink-0 items-center justify-center border-b border-theme-border bg-white px-4 py-2.5">
    <div className="relative flex w-full max-w-[min(20rem,100%)] items-center rounded-xl bg-[#f1f3f5] p-1 shadow-inner">
      <span
        className="absolute top-1 bottom-1 rounded-lg bg-[#749B8F] shadow-md transition-all duration-300 ease-in-out"
        style={{
          width: "calc(50% - 4px)",
          left: active === "chat" ? "4px" : "calc(50% + 0px)",
        }}
      />
      <button
        type="button"
        onClick={() => onChange("chat")}
        className={`relative z-10 flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-lg px-1 py-2.5 text-[13px] font-bold tracking-wide transition-colors duration-200 sm:gap-2 ${
          active === "chat" ? "text-white" : "text-[#64748b] hover:text-[#475569]"
        }`}
        aria-pressed={active === "chat"}
      >
        <span className="material-icons shrink-0 text-[18px]">chat</span>
        <span>Chat</span>
      </button>
      <button
        type="button"
        onClick={() => onChange("side")}
        className={`relative z-10 flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-lg px-1 py-2.5 text-[13px] font-bold tracking-wide transition-colors duration-200 sm:gap-2 ${
          active === "side" ? "text-white" : "text-[#64748b] hover:text-[#475569]"
        }`}
        aria-pressed={active === "side"}
      >
        <span className="material-icons shrink-0 text-[18px]">{sideMaterialIcon}</span>
        <span className="max-w-[7rem] truncate sm:max-w-none">{sideLabel}</span>
      </button>
    </div>
  </div>
);

/* ────────────────────────────────────────────────────────────
   RightPanelHost — Video: full-area Chat | Video toggle only.
   Canvas (social posts) and Blog (WordPress): desktop split + resize;
   compact: Chat | Canvas/Blog toggle.
   ──────────────────────────────────────────────────────────── */
export const RightPanelHost: React.FC<RightPanelHostProps> = ({
  rightPanelMode,
  canvasRequestSignal = 0,
  splitContainerRef,
  chatWidthPercent,
  setIsResizing,
  splitResizeClass,
  surfaceBg,
  chatContent,
  videoPanel,
  socialPostsPanel,
  wordpressBlogPanel,
  videoDockView = "side",
  onVideoDockViewChange,
}) => {
  const [isCompactScreen, setIsCompactScreen] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.innerWidth < 1280 : false
  );
  const [primaryView, setPrimaryView] = useState<"chat" | "side">("chat");

  /** Keeps panel mounted briefly after close so exit animation can finish */
  const [displayMode, setDisplayMode] = useState<"video" | "social_posts" | "wordpress_blog" | null>(rightPanelMode);
  /** Slide / width animation: always false first when opening, then true after a frame */
  const [isOpen, setIsOpen] = useState(false);

  /** Effective panel while opening (displayMode may lag) or closing (displayMode retained) */
  const panelKind = rightPanelMode ?? displayMode;

  useLayoutEffect(() => {
    if (rightPanelMode) {
      setDisplayMode(rightPanelMode);
      setIsOpen(false);
      let innerRaf = 0;
      const outerRaf = requestAnimationFrame(() => {
        innerRaf = requestAnimationFrame(() => setIsOpen(true));
      });
      return () => {
        cancelAnimationFrame(outerRaf);
        cancelAnimationFrame(innerRaf);
      };
    }
    setIsOpen(false);
    setPrimaryView("chat");
    const t = window.setTimeout(() => setDisplayMode(null), 550);
    return () => clearTimeout(t);
  }, [rightPanelMode]);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1279px)");
    const handler = (e: MediaQueryListEvent | MediaQueryList) => setIsCompactScreen(e.matches);
    handler(mql);
    mql.addEventListener("change", handler as EventListener);
    return () => mql.removeEventListener("change", handler as EventListener);
  }, []);

  // Compact: show side panel when canvas/blog opens or user jumps to a post
  useEffect(() => {
    if (rightPanelMode === "social_posts" || rightPanelMode === "wordpress_blog") {
      setPrimaryView("side");
    }
  }, [rightPanelMode, canvasRequestSignal]);

  /* ── No right panel → full-width chat ── */
  if (!panelKind) {
    return (
      <main className={`relative flex h-full flex-1 flex-col overflow-hidden ${surfaceBg}`}>
        {chatContent}
      </main>
    );
  }

  const sideTabLabel: "Video" | "Canvas" | "Blog" =
    panelKind === "video" ? "Video" : panelKind === "wordpress_blog" ? "Blog" : "Canvas";
  const sideTabIcon: "movie" | "dashboard" | "article" =
    panelKind === "video" ? "movie" : panelKind === "wordpress_blog" ? "article" : "dashboard";

  /** Side-panel content for split / compact layouts (non-video modes). */
  const sidePanelContent = panelKind === "wordpress_blog" ? wordpressBlogPanel : socialPostsPanel;

  const setDock = onVideoDockViewChange ?? (() => {});
  const sideVisible = videoDockView === "side" && isOpen;

  /** Video creation: full-area; compact Chat|Video toggle lives in modal header + floating on chat. */
  if (panelKind === "video") {
    return (
      <div className={`relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${surfaceBg}`}>
        {videoDockView === "chat" && (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-30 flex justify-end px-3 pt-2 sm:px-4 sm:pt-3">
            <div className="pointer-events-auto">
              <VideoDockToggle view={videoDockView} onChange={setDock} />
            </div>
          </div>
        )}

        <div ref={splitContainerRef} className="relative min-h-0 flex-1 overflow-hidden">
          <div
            className={`absolute inset-0 flex flex-col overflow-hidden transition-opacity duration-300 ease-out ${
              videoDockView === "chat"
                ? "z-20 opacity-100 pointer-events-auto"
                : "z-0 opacity-0 pointer-events-none"
            }`}
            aria-hidden={videoDockView !== "chat"}
          >
            <main className={`relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${surfaceBg}`}>
              {chatContent}
            </main>
          </div>

          <div
            className={`absolute inset-0 flex flex-col overflow-hidden transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform ${
              sideVisible ? "z-20 translate-x-0" : "z-10 translate-x-full pointer-events-none"
            }`}
            aria-hidden={!sideVisible}
          >
            <aside className={`flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${surfaceBg}`}>
              {videoPanel}
            </aside>
          </div>
        </div>
      </div>
    );
  }

  /* ── Canvas / social posts / wordpress blog: desktop = split + resize (no top toggle) ── */
  if (!isCompactScreen) {
    const showSideSplit = isOpen;
    return (
      <div ref={splitContainerRef} className="flex flex-1 min-h-0 h-full w-full overflow-hidden">
        <main
          style={{ width: showSideSplit ? `${chatWidthPercent}%` : "100%", minWidth: 0 }}
          className={`relative flex h-full min-w-0 flex-shrink-0 flex-col overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${surfaceBg}`}
        >
          {chatContent}
        </main>
        <div
          onMouseDown={() => setIsResizing(true)}
          className={`w-2 flex-shrink-0 cursor-col-resize transition-all duration-500 group flex items-center justify-center ${splitResizeClass} ${
            !showSideSplit ? "pointer-events-none opacity-0 !w-0 overflow-hidden" : "opacity-100"
          }`}
          role="separator"
          aria-valuenow={chatWidthPercent}
          aria-valuemin={25}
          aria-valuemax={75}
        >
          <div className="h-8 w-0.5 rounded-full bg-theme-border group-hover:bg-theme-fg/35" />
        </div>
        <aside
          style={{
            width: showSideSplit ? `${100 - chatWidthPercent}%` : "0%",
            minWidth: 0,
            opacity: showSideSplit ? 1 : 0,
            transform: showSideSplit ? "translate3d(0,0,0)" : "translate3d(2.5rem,0,0)",
          }}
          className={`flex h-full min-h-0 min-w-0 flex-shrink-0 flex-col overflow-hidden transition-[width,opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform ${surfaceBg}`}
        >
          {sidePanelContent}
        </aside>
      </div>
    );
  }

  /* ── Canvas/Blog: compact — Chat | Canvas/Blog toggle + slide ── */
  return (
    <div className={`flex h-full min-w-0 flex-1 flex-col overflow-hidden ${surfaceBg}`}>
      <SegmentedControl
        active={primaryView}
        onChange={setPrimaryView}
        sideLabel={sideTabLabel}
        sideMaterialIcon={sideTabIcon}
      />

      <div ref={splitContainerRef} className="relative min-h-0 flex-1 overflow-hidden">
        <div
          className={`absolute inset-0 transition-opacity duration-200 ease-in-out ${
            primaryView === "chat" ? "z-10 opacity-100 pointer-events-auto" : "z-0 opacity-0 pointer-events-none"
          }`}
        >
          <main className={`relative flex h-full flex-1 flex-col overflow-hidden ${surfaceBg}`}>{chatContent}</main>
        </div>

        <div
          className={`absolute inset-0 overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform ${
            primaryView === "side" && isOpen
              ? "translate-x-0 opacity-100 z-10 pointer-events-auto"
              : "translate-x-full opacity-0 z-0 pointer-events-none"
          }`}
        >
          <aside className={`flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${surfaceBg}`}>
            {sidePanelContent}
          </aside>
        </div>
      </div>
    </div>
  );
};
